import { useCallback, useContext, useMemo } from 'react';

import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { usePlumbing } from '~/api/easy-estimator-api/plumbing-api/usePlumbing';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { IMAGE_COMING_SOON, UNIT_MEASURE } from '~/core/constants';
import { PlumbingProduct } from '~/core/models';
import {
  formatPriceUnitOrFree,
  getProductDescription,
  imgUrl,
} from '~/core/utils';
import { DrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  addSelectedPlumbing,
  removeSelectedPlumbing,
  selectSelectedPlumbing,
  setPlumbingQuantity,
} from '~/redux/slices/plumbing/plumbing.slice';
import { selectZipcode } from '~/redux/slices/zipcode';

type PlumbingDisplayData = {
  title: string;
  image: string;
  price: string;
  quantity: number;
  isSelected: boolean;
};

const useSidePanelPlumbing = () => {
  const { plumbingItems, isFetchingPlumbing } = usePlumbing();
  const zipcode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedMaterial = useSelectedMaterial();
  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useContext(DrawingShapeContext);

  const selectedPlumbing = useAppSelector(selectSelectedPlumbing);
  const dispatch = useAppDispatch();

  const arePlumbingAvailable = useMemo(
    () =>
      Boolean(
        zipcode &&
          selectedStore &&
          selectedMaterial &&
          areThereShapesInTheDrawingArea &&
          plumbingItems.length > 0
      ),
    [
      zipcode,
      selectedStore,
      selectedMaterial,
      areThereShapesInTheDrawingArea,
      plumbingItems.length,
    ]
  );

  const plumbingDisplayData = useMemo(
    () =>
      plumbingItems.map<[PlumbingProduct, PlumbingDisplayData]>((product) => {
        const item = selectedPlumbing[product.productId];
        const quantity = item?.quantity ?? 0;

        return [
          product,
          {
            title: getProductDescription(product),
            image: imgUrl(product.imageUrl ?? IMAGE_COMING_SOON, 300),
            price: formatPriceUnitOrFree(
              product.price,
              product.unitOfMeasure || UNIT_MEASURE.OVERSIZE,
              true
            ),
            quantity,
            isSelected: item?.isSelected ?? false,
          },
        ];
      }),
    [plumbingItems, selectedPlumbing]
  );

  const handlePlumbingClick = useCallback(
    (productId: PlumbingProduct['productId']) => () => {
      const item = selectedPlumbing[productId];
      const product = plumbingItems.find((p) => p.productId === productId);

      if (!product) {
        return;
      }

      if (item) {
        dispatch(removeSelectedPlumbing(product));
      } else {
        dispatch(addSelectedPlumbing(product));
      }

      if (item?.isSelected) {
        dispatch(removeSelectedPlumbing(product));

        return;
      }

      dispatch(addSelectedPlumbing(product));
    },
    [dispatch, plumbingItems, selectedPlumbing]
  );

  const increaseQuantity = useCallback(
    (productId: PlumbingProduct['productId']) => () => {
      const item = selectedPlumbing[productId];

      if (!item) {
        const product = plumbingItems.find((p) => p.productId === productId);

        if (product) {
          dispatch(addSelectedPlumbing(product));
        }

        return;
      }

      const currentQty = item.quantity ?? 0;

      dispatch(setPlumbingQuantity({ productId, quantity: currentQty + 1 }));
    },
    [dispatch, plumbingItems, selectedPlumbing]
  );

  const decreaseQuantity = useCallback(
    (productId: PlumbingProduct['productId']) => () => {
      const item = selectedPlumbing[productId];
      const currentQty = item?.quantity ?? 0;

      if (currentQty > 1) {
        dispatch(setPlumbingQuantity({ productId, quantity: currentQty - 1 }));
      } else if (currentQty === 1) {
        const product = plumbingItems.find((p) => p.productId === productId);

        if (product) {
          dispatch(removeSelectedPlumbing(product));
        }
      }
    },
    [dispatch, plumbingItems, selectedPlumbing]
  );

  const setQuantity = useCallback(
    (productId: PlumbingProduct['productId']) => (qty: number) => {
      if (qty === 0) {
        const product = plumbingItems.find((p) => p.productId === productId);

        if (product) {
          dispatch(removeSelectedPlumbing(product));
        }
      } else {
        dispatch(setPlumbingQuantity({ productId, quantity: qty }));
      }
    },
    [dispatch, plumbingItems]
  );

  return {
    plumbingDisplayData,
    isFetchingPlumbing,
    arePlumbingAvailable,
    handlePlumbingClick,
    increaseQuantity,
    decreaseQuantity,
    setQuantity,
  };
};

export default useSidePanelPlumbing;
